// Player-facing numbers and the goal chip text (IMPLEMENTATION §2.6 "Goal chip", R16 / R17, UX §2). PURE:
// every string comes from copy.json, every number format from ui.json numberFormat; the sim's views
// (query.goalView, UpgradeOption, Bottleneck) are only read.
//   chip        one line, 15 px / 600: `▶ {goal} · {price} · {eta}`; a goal whose copy holds {price} gets
//               `▶ {goal} · {eta}`; an income goal `▶ Reach $1.00K/s auto · $123/s`; no price → `▶ {goal}`
//   substitution the R17 v2 best next step (goalView.advice) with its own price / ETA; the R17 walk
//               (goalView.bottleneck) without a price; the reason line goes on the goal card, never the chip
//   tone        'go' when affordable (green, 1 Hz pulse), 'met' for a stale "Build X" goal with no advice
//               (greyed, M4a #4 / M4c #4), else 'normal'
//   ghost       the goal's item is placed as a ghost: `▶ Crate needs 2× Lead Chunk · 0/2` (copy
//               goal.ghost + goal.ghostFill; its first open need), tone 'normal' — never the next copy's
//               price (playtest fix)
import type { CopyData, UiData } from '../data/schema.ts';
import type { Names } from '../input/preview.ts';
import { formatNum } from '../sim/core/num.ts';
import type { SimDefs } from '../sim/defs.ts';
import type { FamId } from '../sim/gen/ids.ts';
import {
  type Bottleneck,
  type EtaCase,
  type EtaFormat,
  etaOf,
  type GoalGhost,
  type GoalView,
  type UpgradeOption,
} from '../sim/query.ts';
import { copyRaw, copyText, fillCopy } from './copy.ts';

export interface Fmt {
  copy: CopyData;
  numberFormat: UiData['numberFormat'];
  names: Names;
  defs: SimDefs;
  /** ui.json goalChip (ETA thresholds). */
  eta: EtaFormat;
}

/** "$1.23K" (copy hud.money; formatNum truncates, keeps 3 significant figures). */
export function money(f: Pick<Fmt, 'copy' | 'numberFormat'>, n: number): string {
  return fillCopy(f.copy.hud.money ?? '{n}', { n: formatNum(Math.floor(n), f.numberFormat) });
}

/** "$38/s" (copy hud.perSec). */
export function perSec(f: Pick<Fmt, 'copy' | 'numberFormat'>, n: number): string {
  return fillCopy(f.copy.hud.perSec ?? '{rate}/s', { rate: money(f, n) });
}

/** `now!` / `~40s` / `~3m` / `>10m` (copy goal.*, the EtaCase from the sim's goal / shop views). */
export function etaText(copy: CopyData, eta: EtaCase): string {
  const g = copy.goal;
  switch (eta.k) {
    case 'now':
      return g.now ?? 'now!';
    case 'seconds':
      return fillCopy(g.seconds ?? '~{n}s', { n: eta.n });
    case 'minutes':
      return fillCopy(g.minutes ?? '~{n}m', { n: eta.n });
    case 'long':
      return fillCopy(g.long ?? '>{n}m', { n: eta.n });
  }
}

/** The ETA case of `sec` seconds (shop / Expansion cards carry seconds): the sim's own rule (query.etaOf). */
export function etaFromSec(sec: number | null, fmt: EtaFormat): EtaCase {
  return etaOf(sec, fmt);
}

/** The material word of an ore family for the reason line ("lead"), via copy goal.res_<mat>. */
function resOf(f: Fmt, fam: FamId | null): string {
  if (fam === null) return '';
  const dep = f.defs.deposits.find((d) => d.fam === fam);
  return (dep ? f.copy.goal[`res_${dep.mat}`] : undefined) ?? f.names.fam(fam);
}

/** "Upgrade Lead Vein" / "Add a Drill to Copper Vein" (R17 v2 option). */
export function adviceAction(f: Fmt, o: UpgradeOption): string {
  if (o.k === 'drill')
    return fillCopy(f.copy.goal.addDrill ?? '', { machine: f.names.building('deposit', o.ore) });
  return fillCopy(f.copy.goal.upgrade ?? '', { machine: f.names.building(o.type, o.ore) });
}

/** The goal card's reason line: "Upgrade Lead Vein: lead is your bottleneck" (copy goal.why*). */
export function adviceReason(f: Fmt, o: UpgradeOption): string {
  return copyText(f.copy, o.reason, { action: adviceAction(f, o), res: resOf(f, o.fam) });
}

/** "Upgrade Smelter" / "Feed Smelter: needs Copper" (the R17 walk, M4a). */
export function bottleneckAction(f: Fmt, b: Bottleneck): string {
  const machine = f.names.building(b.type, null);
  if (b.k === 'upgrade') return fillCopy(f.copy.goal.upgrade ?? '', { machine });
  return fillCopy(f.copy.goal.feed ?? '', { machine, fam: f.names.fam(b.fam) });
}

export type ChipTone = 'normal' | 'go' | 'met';

export interface ChipView {
  /** The chip's one line. */
  text: string;
  tone: ChipTone;
  /** Goal card title (the action, without price / ETA). */
  title: string;
  /** Goal card: price · ETA (null = no price). */
  priceLine: string | null;
  /** Goal card: why the chip shows this step (advice / bottleneck), else null. */
  reason: string | null;
  /** Goal card: how-to line of the scripted goal (copy unlock.<id>Help), else null. */
  help: string | null;
  /** Goal card: the Loader checklist (u16), "Round parts: Slug ☑ Casing ☐ Powder ☐". */
  checklist: { name: string; made: boolean }[];
}

/**
 * The goal ghost's next need, "2× Lead Chunk" (copy cards.reqItem): the first open requirement only, so the
 * chip fits at 360 px (the ghost's card lists them all); '' when nothing is open.
 */
export function ghostItems(f: Fmt, g: GoalGhost): string {
  const r = g.rows.find((x) => x.filled < x.n);
  if (r === undefined) return '';
  return fillCopy(f.copy.cards.reqItem ?? '{n}× {name}', {
    n: r.n - r.filled,
    name: f.names.item(r.fam, r.tier),
  });
}

/** The ghost chip: title "Crate needs 2× Lead Chunk" and its fill "0/2". */
function ghostChip(f: Fmt, g: GoalGhost, name: string): { title: string; fill: string } {
  let filled = 0;
  let n = 0;
  for (const r of g.rows) {
    filled += Math.min(r.filled, r.n);
    n += r.n;
  }
  return {
    title: fillCopy(f.copy.goal.ghost ?? '{items}', { items: ghostItems(f, g), name }),
    fill: fillCopy(f.copy.goal.ghostFill ?? '{filled}/{n}', { filled, n }),
  };
}

function line(copy: CopyData, goal: string, price: string | null, eta: string | null): string {
  const g = copy.goal;
  if (price !== null && eta !== null)
    return fillCopy(g.chip ?? '▶ {goal} · {price} · {eta}', { goal, price, eta });
  if (eta !== null) return fillCopy(g.chipEta ?? '▶ {goal} · {eta}', { goal, eta });
  return fillCopy(g.chipGoal ?? '▶ {goal}', { goal });
}

/**
 * The goal chip for a goal view. `intro` = how many of the goal's earlier chips have been shown (a goal
 * with several chips, u10 "Now it digs itself!" → "Expand the workshop · $1.00K", shows each earlier one
 * for ui.json goalChip.introMs, without price, then its standing chip).
 */
export function chipView(f: Fmt, v: GoalView, intro = Number.POSITIVE_INFINITY): ChipView {
  const copy = f.copy;
  const checklist = v.checklist.map((c) => ({ name: f.names.item(c.fam, 1), made: c.made }));
  const helpKey = `${v.unlock.id}Help`;
  const help = Object.hasOwn(copy.unlock, helpKey) ? (copy.unlock[helpKey] ?? null) : null;
  if (v.ghost && v.target?.type === 'shop') {
    const g = ghostChip(f, v.ghost, f.names.shop(v.target.id));
    return {
      text: line(copy, g.title, null, g.fill),
      tone: 'normal',
      title: g.title,
      priceLine: null,
      reason: null,
      help,
      checklist,
    };
  }
  if (v.advice) {
    const a = v.advice;
    const title = adviceAction(f, a.option);
    const eta = etaText(copy, a.eta);
    return {
      text: line(copy, title, money(f, a.price), eta),
      tone: a.affordable ? 'go' : 'normal',
      title,
      priceLine: fillCopy(copy.goal.cardPrice ?? '{price} · {eta}', {
        price: money(f, a.price),
        eta,
      }),
      reason: adviceReason(f, a.option),
      help: null,
      checklist,
    };
  }
  if (v.bottleneck) {
    const title = bottleneckAction(f, v.bottleneck);
    return {
      text: line(copy, title, null, null),
      tone: 'normal',
      title,
      priceLine: null,
      reason: fillCopy(copy.goal.bottleneck ?? '{action}', { action: title }),
      help: null,
      checklist,
    };
  }
  const last = v.chips.length - 1;
  const idx = Math.max(0, Math.min(last, intro));
  const key = v.chips[idx] ?? '';
  const standing = idx >= last;
  const price = v.price !== null ? money(f, v.price) : null;
  const incomeTarget = v.income !== null ? perSec(f, v.income.target) : null;
  const title = copyText(copy, key, {
    ...(price !== null ? { price } : {}),
    ...(incomeTarget !== null ? { income: incomeTarget } : {}),
  });
  const tone: ChipTone = v.met ? 'met' : v.affordable ? 'go' : 'normal';
  if (!standing) {
    return {
      text: line(copy, title, null, null),
      tone,
      title,
      priceLine: null,
      reason: null,
      help,
      checklist,
    };
  }
  let text: string;
  let priceLine: string | null = null;
  if (v.income !== null) {
    const now = fillCopy(copy.goal.income ?? '{current}/s', {
      current: money(f, v.income.current),
    });
    text = line(copy, title, null, now);
    priceLine = now;
  } else if (price !== null && v.eta !== null) {
    const eta = etaText(copy, v.eta);
    const inCopy = (copyRaw(copy, key) ?? '').includes('{price}');
    text = inCopy ? line(copy, title, null, eta) : line(copy, title, price, eta);
    priceLine = fillCopy(copy.goal.cardPrice ?? '{price} · {eta}', { price, eta });
  } else {
    text = line(copy, title, null, null);
  }
  return { text, tone, title, priceLine, reason: null, help, checklist };
}

/**
 * Every goal-chip line the data can produce, with the widest price (the biggest compiled Shop price) and the
 * widest ETAs: each unlock's chips (the earlier ones bare, the standing one priced / with its income), the
 * R17 v2 advice actions for every building / deposit and the R17 walk's "Upgrade X" / "Feed X: needs Y".
 * The M7 e2e renders each in the chip at the 360 px layout and asserts no overflow (M4c decision 12).
 */
export function allChipTexts(f: Fmt): string[] {
  const d = f.defs;
  const copy = f.copy;
  let maxPrice = 0;
  for (const it of d.shop) for (const p of it.prices) maxPrice = Math.max(maxPrice, p);
  for (const e of d.board.expansions) maxPrice = Math.max(maxPrice, e.cost);
  const price = money(f, maxPrice);
  const etas = [
    etaText(copy, { k: 'long', n: f.eta.etaMinutesMax }),
    etaText(copy, { k: 'minutes', n: f.eta.etaMinutesMax }),
    etaText(copy, { k: 'seconds', n: f.eta.etaSecondsBelow - 1 }),
  ];
  const out: string[] = [];
  const add = (t: string): void => {
    if (!out.includes(t)) out.push(t);
  };
  for (const u of d.unlocks) {
    u.chips.forEach((key, i) => {
      const standing = i === u.chips.length - 1;
      const raw = copyRaw(copy, key) ?? '';
      if (!standing || u.goal === null) {
        add(line(copy, copyText(copy, key, { price, income: perSec(f, maxPrice) }), null, null));
        return;
      }
      if (u.goal.type === 'autoIncome') {
        const title = copyText(copy, key, { income: perSec(f, u.goal.perSec) });
        const now = fillCopy(copy.goal.income ?? '{current}/s', {
          current: money(f, u.goal.perSec),
        });
        add(line(copy, title, null, now));
        return;
      }
      const title = copyText(copy, key, { price });
      for (const eta of etas)
        add(raw.includes('{price}') ? line(copy, title, null, eta) : line(copy, title, price, eta));
    });
  }
  const types = Object.keys(d.buildings) as (keyof typeof d.buildings)[];
  for (const t of types) {
    const machine = f.names.building(t, null);
    for (const eta of etas)
      add(line(copy, fillCopy(copy.goal.upgrade ?? '', { machine }), price, eta));
    add(line(copy, fillCopy(copy.goal.upgrade ?? '', { machine }), null, null));
    // the R17 walk feeds a producer with what its recipes (or the Smelter's fuel) take
    const def = d.buildings[t] as {
      recipe?: string;
      modes?: { recipes: string[] }[];
      slots?: string[];
    };
    const ids = [
      ...(def.recipe ? [def.recipe] : []),
      ...(def.modes ?? []).flatMap((m) => m.recipes),
    ];
    const fams: string[] = [
      ...ids.flatMap((id) => d.recipes.find((r) => r.id === id)?.inputs ?? []),
      ...(def.slots ?? []),
      ...(t === 'smelter' ? [d.buildings.smelter.fuel] : []),
    ];
    for (const fam of d.families)
      if (fams.includes(fam.id))
        add(
          line(
            copy,
            fillCopy(copy.goal.feed ?? '', { machine, fam: f.names.fam(fam.id) }),
            null,
            null,
          ),
        );
  }
  // a goal item placed as a ghost: every requirement still open (the widest need), 0 filled
  for (const u of d.unlocks) {
    if (u.goal === null || u.goal.type !== 'shop') continue;
    const goal = u.goal;
    const item = d.shop.find((x) => x.id === goal.id);
    if (!item || item.kind === 'deposit') continue;
    const reqs = d.buildings[item.ref as keyof typeof d.buildings].ghost;
    if (reqs.length === 0) continue;
    // each requirement as the first open one (the ones before it delivered)
    reqs.forEach((_, i) => {
      const rows = reqs.map((r, j) => ({ ...r, filled: j < i ? r.n : 0 }));
      const c = ghostChip(f, { bid: 0, x: 0, y: 0, rows }, f.names.shop(item.id));
      add(line(copy, c.title, null, c.fill));
    });
  }
  for (const dep of d.deposits) {
    const machine = f.names.building('deposit', dep.id);
    for (const eta of etas) {
      add(line(copy, fillCopy(copy.goal.upgrade ?? '', { machine }), price, eta));
      add(line(copy, fillCopy(copy.goal.addDrill ?? '', { machine }), price, eta));
    }
  }
  return out;
}
